<template>
  <div class="report-sources-sales">
    <div class="report-sources-sales__header">
      <h1 class="report-sources-sales__title">Источники</h1>
      <div class="report-sources-sales__form">
        <div class="report-sources-sales__row">
          <date-picker-dialog
            range
            v-bind:value="period"
            v-on:input="onInputPeriod"
            v-on:close="onCloseDatePickerDialog"
          >
            <template v-slot:trigger="{ listenerObject }">
              <button
                type="button"
                v-on="listenerObject"
                class="report-sources-sales__search"
              >
                <div v-if="period.length > 0">{{ periodTitle }}</div>
                <div v-else>Выберите период</div>
              </button>
            </template>
          </date-picker-dialog>
        </div>
        <div class="report-sources-sales__tags">
          <button
            type="button"
            v-on:click="setPeriodByYesterday"
            class="report-sources-sales__tag"
            v-bind:class="{ active: periodName === $options.periodYesterday }"
          >Вчера</button>
          <button
            type="button"
            v-on:click="setPeriodByWeek"
            class="report-sources-sales__tag"
            v-bind:class="{ active: periodName === $options.periodWeek }"
          >Неделя</button>
          <button
            type="button"
            v-on:click="setPeriodByMonth"
            class="report-sources-sales__tag"
            v-bind:class="{ active: periodName === $options.periodMonth }"
          >Mесяц</button>
          <button
            type="button"
            v-on:click="setPeriodByYear"
            class="report-sources-sales__tag"
            v-bind:class="{ active: periodName === $options.periodYear }"
          >Год</button>
        </div>
        <div>
          <div>
            <multiple-autocomplete-input
              v-model="selectedSources"
              chips
              class="report-sources-sales__autocomplete"
              item-text="name"
              item-value="id"
              label="Выберите источники"
              multiple
              removal-per-chip
              set-select-all-option
              small-chips
              v-bind:items="sourcesFilter"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="report-sources-sales__body">
      <pie-and-bar-diagram
        item-label-key="groupName"
        item-value-key="summary"
        v-bind:items="reportLinesSortedBySummary"
      />
      <button
        type="button"
        v-on:click="fetchReportSpreadsheet"
        class="report-sources-sales__button">EXСEL</button>
      <report-table
        v-bind:report-lines="reportLines"
        v-bind:need-clear-sorts="needClearSorts"
        v-bind:initial-sort-type="selectedSortType"
        v-on:input="onChangeSort"
      />
    </div>
  </div>
</template>

<script>

import DatePickerDialog from '@/components/DatePickerDialog.vue';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';
import debounce from 'lodash.debounce';
import PieAndBarDiagram from '@/components/PieAndBarDiagram.vue';
import ReportTable from '@/components/ReportTable.vue';
import notifierMixin from '@/mixins/notifierMixin';
import reportSalesMixin from '@/mixins/reportSalesMixin';

/**
 * @description Типы отчетов
 * @type {Array}
 */
const reportTypes = [
  { value: 'category', title: 'По отделениям' },
  { value: 'service', title: 'По услугам' },
];

/**
 * Часть названия отчета: Отчет по источникам
 *
 * @type {string}
 */
const PART_OF_REPORT_REPORT_BY_SOURCES = 'Отчет по источникам';

export default {
  components: {
    ReportTable,
    PieAndBarDiagram,
    MultipleAutocompleteInput,
    DatePickerDialog,
  },
  computed: {
    /**
     * @description Отсортированные по сумме линии отчета
     * @param reportLines
     * @return {*[]}
     */
    reportLinesSortedBySummary({ reportLines }) {
      const sortedReportLines = [...reportLines];
      return sortedReportLines.sort((line1, line2) => {
        if (line1.summary < line2.summary) {
          return 1;
        }
        if (line1.summary > line2.summary) {
          return -1;
        }
        return 0;
      });
    },
    /**
     * @description Запрос.
     * @type {any}
     */
    viewQuery() {
      return {
        period: this.period,
        sources: this.selectedSources,
      };
    },
  },
  async created() {
    const result = await this.fetchFilter();
    result.forEach(() => {
      this.setPeriodByMonth();
      this.selectedSources = this.sourcesFilter.map(({ id }) => id);
    });
  },
  data() {
    return {
      /** Наименование филиала клиники * */
      branchName: '',
      diagramType: 'pie',
      needClearSorts: false,
      reportLines: [],
      reportType: 'category',
      selectedSources: [],
      showDiagram: false,
      sortByDebt: '',
      sortByDiscount: '',
      sortByPaidAmount: '',
      sortBySummary: '',
      sortByTotalPrice: '',
      selectedSortType: '',
      sourcesFilter: [],
    };
  },
  methods: {
    /**
     * @description Очистить сортировки таблицы
     */
    clearTableSorts() {
      this.needClearSorts = true;
      this.$nextTick(() => {
        this.needClearSorts = false;
      });
    },
    /**
     * @description Затянуть фильтр.
     * @returns {Promise}
     */
    fetchFilter() {
      const onSuccess = (filters) => {
        this.branchName = filters.branch_name;
        this.sourcesFilter = filters.sources;
      };
      const onFail = (error) => {
        this.notifyFail(error.message);
      };
      const result = this.$reportGateway.getFilterForReportSourcesSales();
      result.then((either) => either.fold(onFail, onSuccess));
      return result;
    },
    /**
     * @description Создать сортировку.
     * @param {string} type       Тип сортировки.
     * @param {string} direction  Направление.
     * @returns {{type: string, direction: string}}
     */
    makeSort(type = '', direction = '') {
      this.selectedSortType = type;
      if (type === '' && direction === '') {
        return {
          type: 'sortByTotalPrice',
          direction: 'desc',
        };
      }
      if (type !== '' || direction !== '') {
        this.clearTableSorts();
      }
      return {
        type,
        direction,
      };
    },
    /**
     * @description Затянуть отчет
     */
    fetchReport(type = '', direction = '') {
      const onSuccess = (lines) => {
        this.reportLines = lines;
      };
      const onFail = (error) => {
        this.notifyFail(error.message);
      };
      if (
        this.period.length < 2
        || !this.selectedSources.length
      ) {
        return;
      }
      const period = this.period.map((date) => date.toISOString().split('T')[0]);
      const sort = this.makeSort(type, direction);
      this
        .$reportGateway
        .getReportSalesSources(period, this.selectedSources, sort)
        .then((either) => either.fold(onFail, onSuccess));
    },
    /**
     * @description затянуть таблицу отчета
     */
    fetchReportSpreadsheet() {
      const reportName = this.getReportFileName();
      const onSuccess = (file) => {
        this.saveFile(file, reportName);
      };
      const onFail = (error) => {
        this.notifyFail(error.message);
      };
      if (this.period.length < 2) {
        return;
      }
      const period = this.period.map((date) => date.toISOString().split('T')[0]);
      this
        .$reportGateway
        .getReportSalesSourcesBySheet(period, this.selectedSources)
        .then((either) => either.fold(onFail, onSuccess));
    },
    /**
     * @description Сформировать название отчета для файла.
     * @returns {string}
     */
    getReportFileName() {
      const parts = [];
      parts.push(PART_OF_REPORT_REPORT_BY_SOURCES);
      if (this.branchName && this.branchName.trim() !== '') {
        parts.push(this.branchName.trim());
      }
      if (this.period.length >= 2) {
        const [dateFrom, dateTo] = this
          .period
          .map((date) => date.toISOString().split('T')[0]);
        parts.push(`${dateFrom} — ${dateTo}`);
      }
      const fileName = parts
        .filter((part) => part && part.trim() !== '')
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      return `${fileName}.xls`;
    },
    /**
     * @description Изменение сортировки
     * @param sort
     */
    onChangeSort(sort) {
      this.fetchReport(sort.type, sort.direction);
    },
  },
  watch: {
    viewQuery: debounce(function _() {
      this.fetchReport();
    }, 1000),
  },
  mixins: [notifierMixin, reportSalesMixin],
  name: 'ClinicPanelReportSalesSources',
  reportTypes,
};
</script>

<style lang="scss">
@use "@/scss/buttons";
.menuable__content__active {
  min-width: 250px !important;
}
.report-sources-sales {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  &__header {
    background: #F2F5F8;
    width: 100%;
    padding: 38px 20px 48px 23px;
  }
  &__autocomplete {
    min-width: 300px !important;
  }
  &__form {
    display: flex;
    flex-flow: row wrap;
    align-items: flex-end;
    gap: 23px;
    margin: 30px 0 0 0;
  }
  &__row {
    display: flex;
    flex-direction: row;
  }
  &__search {
    border: 1px solid #C2CFE0;
    border-radius: 4px;
    background: #FFFFFF;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    height: 40px;
    min-width: 250px;
    width: 100%;
    padding: 0 0 0 10px;
    display: flex;
    align-items: center;
    position: relative;
    &:after {
      content: '';
      background-color: #EAEFF3;
      border-radius: 0 4px 4px 0;
      width: 38px;
      height: 38px;
      position: absolute;
      right: 0;
      overflow: hidden;
      background-image: url("@/assets/img/calendar-grey.svg");
      background-position: center;
    }
    &__exit {
      width: 40px;
      height: 40px;
      border: 1px solid #C2CFE0;
      background-color: #EAEFF3;
      border-radius: 4px;
      background-image: url("@/assets/img/exit-dark-grey.svg");
      background-position: center;
      margin: 0 0 0 -2px;
    }
  }
  &__tags {
    display: flex;
    flex-direction: row;
    align-items: center;
    border-radius: 4px;
    overflow: hidden;
  }
  &__tag {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    background: #EAEFF3;
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
    color: #192A3E;
    padding: 0 13px;
    &:hover,
    &.active {
      background: #0489E0;
      color: #FFFFFF;
    }
  }
  &__select {
    width: 400px;
  }
  &__body {
    padding: 22px 20px 100px 23px;
  }
  &__button {
    @include buttons.btn-transparent(155px, 48px, 16px);
    margin: 40px 0 30px auto;
    &:before {
      content: "";
      background-image: url("@/assets/img/exel.svg");
      background-repeat: no-repeat;
      background-size: contain;
      width: 16px;
      height: 16px;
      margin: 0 8px 0 0;
    }
  }
}

@media screen and (max-width: 480px) {
  .menuable__content__active {
    min-width: initial;
  }
  .report-sources-sales {
    &__header {
      padding: 40px 15px;
    }
    &__body {
      padding: 22px 15px 50px;
    }
    &__button {
      width: 100%;
    }
    &__autocomplete {
      min-width: initial;
    }
  }
}
</style>
